import React, { useState, useMemo } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, ChevronDown, Star, UserMinus } from 'lucide-react';
import type { Friend } from '../../hooks/useFriends';
import type { WishlistEntry } from '../../types/social';
import { StickerChip } from './StickerChip';
import { Button } from './Button';

interface Props {
  friend: Friend;
  myCounts: Record<string, number>;
  allCodes: string[];
  priorityMatches?: WishlistEntry[];
  onRemove: () => void;
  slotExpanded?: React.ReactNode;
}

const AVATAR_HUES = ['#7B3F00', '#1E40AF', '#0E7490', '#9333EA', '#1B5E20', '#C9A227'];

function avatarColorFromName(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return AVATAR_HUES[Math.abs(h) % AVATAR_HUES.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function FriendCard({
  friend,
  myCounts,
  allCodes,
  priorityMatches = [],
  onRemove,
  slotExpanded,
}: Props) {
  const [open, setOpen] = useState(false);

  const { canGet, canGive } = useMemo(() => {
    const myMissingSet = new Set(allCodes.filter((c) => (myCounts[c] || 0) === 0));
    const myDuplicatesSet = new Set(allCodes.filter((c) => (myCounts[c] || 0) >= 2));
    const friendDuplicates = new Set(allCodes.filter((c) => (friend.counts[c] || 0) >= 2));
    const friendMissing = new Set(allCodes.filter((c) => (friend.counts[c] || 0) === 0));
    const get = allCodes.filter((c) => friendDuplicates.has(c) && myMissingSet.has(c));
    const give = allCodes.filter((c) => myDuplicatesSet.has(c) && friendMissing.has(c));
    return { canGet: get, canGive: give };
  }, [friend, myCounts, allCodes]);

  const hasPriorityMatch = priorityMatches.length > 0;
  const hasMatches = canGet.length > 0 || canGive.length > 0;
  const initials = getInitials(friend.name);
  const color = avatarColorFromName(friend.name);
  const firstName = friend.name.split(/[\s@]/)[0];
  const topMatch = priorityMatches[0];

  return (
    <div
      className={[
        'rounded-xl overflow-hidden',
        hasPriorityMatch
          ? 'bg-gold-100/60 border-2 border-gold-500 shadow-glow-gold'
          : 'bg-white border border-ink-300 shadow-xs',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-transparent border-none cursor-pointer btn-focus"
      >
        <span
          aria-hidden="true"
          className="w-11 h-11 rounded-full flex items-center justify-center font-display font-bold text-white text-[14px] shrink-0"
          style={{ background: color }}
        >
          {initials}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-[17px] text-ink-900 truncate flex items-center gap-1.5">
            {hasPriorityMatch && (
              <Star
                size={15}
                className="text-gold-500 shrink-0"
                fill="currentColor"
                strokeWidth={2}
                aria-label="Tem sua prioridade"
              />
            )}
            <span className="truncate">{friend.name}</span>
          </p>
          {hasPriorityMatch ? (
            <p className="text-caption text-gold-600 font-semibold mt-0.5 truncate">
              {priorityMatches.length === 1
                ? `Tem sua prioridade #${topMatch.priority} (${topMatch.sticker_code})`
                : `Tem ${priorityMatches.length} das suas prioridades`}
            </p>
          ) : hasMatches ? (
            <p className="text-caption text-ink-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
              {canGet.length > 0 && (
                <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                  <ArrowDownToLine size={12} strokeWidth={2.5} />
                  {canGet.length} pra você
                </span>
              )}
              {canGet.length > 0 && canGive.length > 0 && (
                <span className="text-ink-300">·</span>
              )}
              {canGive.length > 0 && (
                <span className="inline-flex items-center gap-1 text-gold-600 font-semibold">
                  <ArrowUpFromLine size={12} strokeWidth={2.5} />
                  {canGive.length} pra ele
                </span>
              )}
            </p>
          ) : (
            <p className="text-caption text-ink-500 mt-0.5">Nenhuma troca possível agora</p>
          )}
        </div>
        <ChevronDown
          size={18}
          className={[
            'text-ink-500 shrink-0 transition-transform duration-base ease-out',
            open ? 'rotate-180' : 'rotate-0',
          ].join(' ')}
        />
      </button>

      {open && (
        <div
          className={[
            'px-4 pb-4 space-y-3 pt-3',
            hasPriorityMatch ? 'border-t border-gold-500/40' : 'border-t border-ink-300/60',
          ].join(' ')}
        >
          {hasPriorityMatch && (
            <div>
              <p className="bt-eyebrow text-gold-600 mb-2 flex items-center gap-1.5">
                <Star size={11} className="text-gold-500" fill="currentColor" strokeWidth={2} />
                Suas prioridades que ele tem ({priorityMatches.length})
              </p>
              <ul className="space-y-1.5">
                {priorityMatches.map((m) => (
                  <li
                    key={m.sticker_code}
                    className="flex items-center gap-2 bg-white border border-gold-500/40 rounded-lg px-2.5 py-1.5"
                  >
                    <span className="w-6 h-6 rounded-full bg-green-700 text-white font-display font-bold flex items-center justify-center text-[11px] shrink-0">
                      {m.priority}
                    </span>
                    <span className="font-mono font-semibold text-ink-900 text-small">
                      {m.sticker_code}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {canGet.length > 0 && (
            <div>
              <p className="bt-eyebrow text-green-700 mb-2">
                {firstName} tem pra te dar ({canGet.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {canGet.map((code) => (
                  <StickerChip key={code} code={code} tone="green" />
                ))}
              </div>
            </div>
          )}
          {canGive.length > 0 && (
            <div>
              <p className="bt-eyebrow text-gold-600 mb-2">
                Você tem pra dar pra {firstName} ({canGive.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {canGive.map((code) => (
                  <StickerChip key={code} code={code} tone="gold" />
                ))}
              </div>
            </div>
          )}
          {!hasMatches && !hasPriorityMatch && (
            <p className="text-small text-ink-500 italic">
              Sem trocas combinando agora — volta depois.
            </p>
          )}
          {slotExpanded}
          <Button
            variant="ghost"
            size="sm"
            icon={UserMinus}
            onClick={onRemove}
            className="text-ink-500 hover:text-danger hover:bg-ruby-100"
          >
            Remover amigo
          </Button>
        </div>
      )}
    </div>
  );
}
